import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { STAGES } from "@/lib/stages";

export default function AdminDeals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/deals")
      .then((r) => { if (!cancelled) { setDeals(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const deleteDeal = async (deal, e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = window.confirm(
      `Permanently delete this deal?\n\n${deal.commodity}\n${deal.deal_id}\nBuyer: ${deal.user_email}\n\nThis cannot be undone.`
    );
    if (!ok) return;
    try {
      await api.delete(`/admin/deals/${deal.deal_id}`);
      setDeals((prev) => prev.filter((d) => d.deal_id !== deal.deal_id));
    } catch (err) {
      window.alert("Delete failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const grouped = STAGES.map((s) => ({
    ...s, deals: deals.filter((d) => d.stage === s.key),
  }));

  return (
    <DashboardLayout testid="admin-deals-page">
      <div className="p-8 md:p-12 max-w-[1600px]">
        <div className="overline">Administration — Deal Flow Pipeline</div>
        <h1 className="h-serif text-5xl text-ink mt-3">All deals</h1>
        <p className="text-sub mt-3 max-w-2xl text-sm">
          Each column is a pipeline stage. Drag your eye left-to-right to see how every
          live deal is progressing toward commission clearing.
        </p>

        {loading ? (
          <div className="mt-10 overline">Loading deals…</div>
        ) : (
          <div className="mt-10 overflow-x-auto pb-6" data-testid="admin-deals-kanban">
            <div className="grid grid-flow-col auto-cols-[280px] gap-4">
              {grouped.map((col) => (
                <Column key={col.key} stage={col} onDelete={deleteDeal} />
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Column({ stage, onDelete }) {
  return (
    <section
      className="bg-parchment2 border border-line min-h-[60vh] flex flex-col"
      data-testid={`column-${stage.key}`}
    >
      <header className="px-4 py-3 border-b border-line bg-parchment flex items-baseline justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-oxblood uppercase">
            {stage.short}
          </div>
          <div className="h-serif text-base text-ink mt-0.5 leading-tight">
            {stage.label}
          </div>
        </div>
        <div className="font-mono text-xs text-sub tabular">
          {stage.deals.length}
        </div>
      </header>
      <div className="flex-1 p-3 space-y-3 overflow-y-auto">
        {stage.deals.length === 0 && (
          <div className="text-[11px] font-mono text-sub uppercase tracking-widest text-center py-6 opacity-60">
            — empty —
          </div>
        )}
        {stage.deals.map((d) => <DealCard key={d.deal_id} deal={d} onDelete={onDelete} />)}
      </div>
    </section>
  );
}

function DealCard({ deal, onDelete }) {
  return (
    <Link
      to={`/admin/deals/${deal.deal_id}`}
      className="block bg-parchment border border-line p-4 hover:border-oxblood transition-colors group relative"
      data-testid={`card-${deal.deal_id}`}
    >
      <div className="h-serif text-base text-ink leading-tight pr-6">{deal.commodity}</div>
      <div className="font-mono text-[10px] text-sub mt-1 truncate">{deal.deal_id}</div>

      <div className="mt-3 text-xs text-sub tabular">
        {Number(deal.volume).toLocaleString()} {deal.unit}
        <span className="text-line mx-1.5">·</span>
        ${Number(deal.target_price).toLocaleString()}
      </div>
      <div className="mt-1 text-[11px] text-sub">
        {deal.incoterm} · {deal.delivery_port}
      </div>

      <div className="mt-3 pt-3 border-t border-line">
        <div className="text-xs text-ink truncate">{deal.user_name}</div>
        <div className="text-[10px] text-sub truncate">{deal.company || deal.user_email}</div>
      </div>

      <button
        onClick={(e) => onDelete(deal, e)}
        className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center text-sub hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity"
        title="Delete deal"
        data-testid={`delete-deal-${deal.deal_id}`}
        aria-label="Delete"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
          <line x1="10" y1="11" x2="10" y2="17"/>
          <line x1="14" y1="11" x2="14" y2="17"/>
        </svg>
      </button>
    </Link>
  );
}
